import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Tag, MagnifyingGlass, HashStraight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price, currency) => {
  if (price === null || price === undefined || price === "") return "-";
  try {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: currency || 'EUR' }).format(price);
  } catch {
    return `${price} ${currency || ''}`;
  }
};

export default function Precos() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [groupCode, setGroupCode] = useState("");
  const [groupResult, setGroupResult] = useState(null);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState("");

  useEffect(() => {
    const code = groupCode.trim();
    if (!code) {
      setGroupResult(null);
      setGroupError("");
      return;
    }
    setGroupLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/price-extras/group-table/lookup`, { params: { code } });
        setGroupResult(response.data);
        setGroupError("");
      } catch (error) {
        setGroupResult(null);
        setGroupError(error?.response?.data?.detail || "Código de grupo não encontrado");
      } finally {
        setGroupLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [groupCode]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/price-products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching price products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filtered = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];
    return products.filter((p) => {
      const haystack = [p.product_name, p.product_code, p.color, p.format, p.series]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return words.every((word) => haystack.includes(word));
    });
  }, [products, search]);

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="precos-page">
      <PageHeader icon={Tag} title="Preços" subtitle="Catálogo de produtos e preços Gresart" />

      {/* Pesquisa por artigo e por código de grupo, lado a lado, metade da largura cada */}
      <div className="flex flex-col sm:flex-row gap-4" data-testid="precos-search-row">
        <div className="relative w-full sm:w-1/2" data-testid="precos-search-container">
          <MagnifyingGlass size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Pesquisa por artigo"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-14 text-base rounded-xl shadow-sm border-stone-200 focus-visible:ring-[#009392]"
            data-testid="precos-search-input"
            autoFocus
          />
        </div>

        <div className="w-full sm:w-1/2" data-testid="precos-group-lookup">
          <div className="relative">
            <HashStraight size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              id="pesquisa-grupo"
              placeholder="Pesquisa por código de grupos"
              value={groupCode}
              onChange={(e) => setGroupCode(e.target.value)}
              className="pl-12 h-14 text-base rounded-xl shadow-sm border-stone-200 focus-visible:ring-[#009392]"
              data-testid="input-pesquisa-grupo"
            />
          </div>

          {groupLoading && (
            <div className="flex items-center gap-2 mt-3 text-sm text-stone-400" data-testid="grupo-a-pesquisar">
              <LoadingSpinner size={16} /> A pesquisar...
            </div>
          )}

          {!groupLoading && groupError && (
            <p className="text-sm text-rose-500 mt-3" data-testid="grupo-nao-encontrado">{groupError}</p>
          )}

          {!groupLoading && groupResult && (
            <div className="mt-4 space-y-3 gresart-card" data-testid="grupo-resultado">
              <div className="flex items-center justify-between bg-[#009392]/5 border border-[#009392]/20 rounded-lg px-4 py-3">
                <div>
                  <p className="text-xs text-stone-400">{groupResult.code}</p>
                  <p className="text-sm text-stone-600">Preço Base {groupResult.unit === "m2" ? "/ m²" : "/ Unidade"}</p>
                </div>
                <span className="text-2xl font-extrabold text-[#009392]" data-testid="grupo-preco-base">
                  {formatPrice(groupResult.price, groupResult.currency)}
                </span>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wide">Preços com Desconto</p>
                {groupResult.discounts.map((tier) => (
                  <div key={tier.label} className="flex items-center justify-between" data-testid={`grupo-desconto-${tier.label.replace(/[\s%+]/g, '')}`}>
                    <span className="text-sm text-stone-500">{tier.label}</span>
                    <span className="text-base font-bold text-stone-900">{formatPrice(tier.price, groupResult.currency)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {search.trim() && (
        loading ? (
          <div className="flex items-center justify-center min-h-[300px]">
            <LoadingSpinner size={40} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state py-16 bg-stone-50 rounded-xl text-center" data-testid="precos-empty-state">
            <Tag size={56} className="text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-stone-700 mb-2">Nenhum produto encontrado</h3>
            <p className="text-stone-500 max-w-md mx-auto">Tente outro nome ou referência de produto</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="precos-list">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/precos/${p.id}`}
                className="gresart-card hover:shadow-lg transition-all"
                data-testid={`preco-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-semibold text-stone-900 leading-snug line-clamp-2">{p.product_name || "Produto sem nome"}</h3>
                </div>
                <p className="text-xs text-stone-400 mb-3">{p.product_code}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#009392]/10 text-[#009392] font-medium truncate max-w-[55%]">
                    {p.series || "-"}
                  </span>
                  <span className="flex items-baseline gap-1 text-lg font-bold text-stone-900">
                    {formatPrice(p.price, p.currency)}
                    {p.price_unit && <span className="text-xs font-normal text-stone-400">/{p.price_unit}</span>}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )
      )}
    </div>
  );
}
